import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import swal from "sweetalert";
import { validateCheckNumbers } from "../../../utils/checkNumberValidator";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import NoData from "../../../assets/img/no-data.png";
import NoAccess from "../../../assets/img/NoAccess.png";
import { Table, Button } from "react-bootstrap";
import { getProfitDistributionById } from "../../../services/ProfitDistribution/getProfitDistributionById.service";
import { getOwnerEquityAccounts } from "../../../services/ProfitDistribution/getOwnerEquityAccounts.service";
import { getFundAllocations } from "../../../services/ProfitDistribution/getFundAllocations.service";
import { saveFundAllocation } from "../../../services/ProfitDistribution/saveFundAllocation.service";
import { resetFundAllocation } from "../../../services/ProfitDistribution/resetFundAllocation.service";
import { decideFundAllocation } from "../../../services/ProfitDistribution/decideFundAllocation.service";
import { getSubjects } from "../../../services/AccountList/getSubjects.service";
import { getSubject3 } from "../../../services/AccountList/getSubject3.service";
import { getUserAccessType } from "../../../services/general/getUserAccessType.service";
import { formatCurrency, formatToPHP } from "../../../utils/numberFormatter";
import ProfitDistributionSummary from "./component/ProfitDistributionSummary";
import {
  canAllocateFunds,
  canDecideFundAllocation,
  getApprovalBlockReason,
  isAllocationApproved,
} from "./component/ProfitDistributionStatus";
import SplitAllocationModal, {
  toCents,
} from "./component/SplitAllocationModal";
import SourceAccountFields, {
  emptySource,
  describeSource,
} from "./component/SourceAccountFields";
import {
  toSplitLine,
  toPayloadLine,
  buildFundAllocationSummary,
  describeOverdrawnAccount,
  getInsufficientBalanceReason,
  getApprovalIssues,
  getApproveConfirmText,
} from "./component/fundAllocation.helper";

function FundAllocation({ authrztn }) {
  const { id } = useParams();
  const userLoggedID = useDecodeToken();

  const [distribution, setDistribution] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDistribution = async () => {
    setIsLoading(true);
    try {
      const data = await getProfitDistributionById(id);
      setDistribution(data);
    } catch (error) {
      console.log(error);
      setDistribution(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Owner's Equity accounts
  const [owners, setOwners] = useState([]);

  const fetchOwners = async () => {
    try {
      const data = await getOwnerEquityAccounts();
      setOwners(data);
    } catch (error) {
      console.log(error);
    }
  };

  // Account-List subjects (e.g. Cash) and their accounts (e.g. Cash on Hand).
  // Accounts are cached per subject and shared with the split modal.
  const [subjects, setSubjects] = useState([]);
  const [accountsBySubject, setAccountsBySubject] = useState({});

  const fetchSubjects = async () => {
    try {
      const data = await getSubjects("Account-List");
      setSubjects(data);
    } catch (error) {
      console.log(error);
    }
  };

  const loadAccounts = async (subjectId) => {
    if (!subjectId || accountsBySubject[subjectId]) return;
    try {
      const data = await getSubject3(subjectId);
      setAccountsBySubject((prev) => ({ ...prev, [subjectId]: data }));
    } catch (error) {
      console.log(error);
    }
  };

  // Access type of the logged in user. A "Management" approval confirms Bank
  // payments without a cheque right away (deducted from the source account)
  const [accessType, setAccessType] = useState("");

  const fetchAccessType = async () => {
    try {
      setAccessType(await getUserAccessType(userLoggedID));
    } catch (error) {
      console.log(error);
    }
  };

  // Default source of funds: used for every owner without a custom split
  const [defaultSource, setDefaultSource] = useState(emptySource);

  const handleDefaultSourceChange = (fields) => {
    setDefaultSource((prev) => ({ ...prev, ...fields }));
  };

  // Custom splits per owner:
  // { [ownerId]: [{ paymentMethod, subject, account, chequeNumber, maturityDate, remarks, amount }] }
  // Saved in the database per owner; loaded when the page opens
  const [splits, setSplits] = useState({});
  const [splitOwner, setSplitOwner] = useState(null);

  // isSaving drives the UI; the ref blocks a second click immediately
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const fetchFundAllocations = async () => {
    try {
      const rows = await getFundAllocations(id);
      const grouped = {};
      rows.forEach((row) => {
        (grouped[row.owner_account_id] ||= []).push(toSplitLine(row));
      });
      setSplits(grouped);

      // Account lists of the saved subjects, so the modal can show them
      new Set(rows.map((row) => row.source_subject_id)).forEach(loadAccounts);
    } catch (error) {
      console.log(error);
    }
  };

  // Runs a save/reset request once at a time, then reloads the saved splits
  const runSplitRequest = async (request, successTitle) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);

    try {
      const result = await request();
      swal({
        icon: "success",
        title: successTitle,
        text: result.message,
        timer: 2000,
        button: false,
      });
      setSplitOwner(null);
      await fetchFundAllocations();
    } catch (error) {
      console.log(error);
      swal({
        icon: "error",
        title: "Something went wrong",
        text:
          error.response?.data?.message ||
          "Unable to save the fund allocation.",
      });
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  // Cheque numbers saved on this profit distribution. Owners of the same
  // distribution may share a cheque (the default source pays several owners),
  // so these are not looked up again; the backend applies the same rule.
  const savedCheques = Object.values(splits)
    .flat()
    .filter((line) => line.paymentMethod === "Bank")
    .map((line) => line.chequeNumber);

  const handleSaveSplit = async (lines) => {
    const cheques = lines
      .filter((line) => line.paymentMethod === "Bank")
      .map((line) => line.chequeNumber);
    const isValid = await validateCheckNumbers(cheques, {
      ignore: savedCheques,
    });
    if (!isValid) return;

    return runSplitRequest(
      () =>
        saveFundAllocation({
          profit_distribution_id: id,
          owner_account_id: splitOwner.id,
          allocated_amount: allocations[splitOwner.id] || 0,
          lines: lines.map(toPayloadLine),
          userLoggedID,
        }),
      "Split saved",
    );
  };

  const handleResetSplit = () => {
    swal({
      title: "Confirm Action",
      text: `Remove the saved split for "${splitOwner.account_name}"? They will use the default source account again.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      runSplitRequest(
        () =>
          resetFundAllocation({
            profit_distribution_id: id,
            owner_account_id: splitOwner.id,
            userLoggedID,
          }),
        "Split reset",
      );
    });
  };

  useEffect(() => {
    fetchDistribution();
    fetchFundAllocations();
  }, [id]);

  useEffect(() => {
    fetchOwners();
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (userLoggedID) fetchAccessType();
  }, [userLoggedID]);

  // Allocations, source lines and approval checks (see fundAllocation.helper)
  // Owners share the amount to distribute entered on the distribution (part
  // of the net income), not always the whole net income
  const netIncome = Number(distribution?.net_income) || 0;
  const summary = buildFundAllocationSummary({
    netIncome: Number(distribution?.amount_to_distribute) || 0,
    owners,
    splits,
    defaultSource,
    isAutoConfirmBank: accessType === "Management",
  });
  const {
    amountToDistribute,
    allocations,
    getOwnerLines,
    getUnallocatedCents,
    unfundedOwners,
    partiallySplitOwners,
    ownersOnDefault,
    isDefaultIncomplete,
    overdrawnAccounts,
  } = summary;

  // Splits can only be changed before the allocation is approved
  const canEdit = canAllocateFunds(distribution);
  const allocationApproved = isAllocationApproved(distribution);

  const canApproveAllocation =
    authrztn.includes("Retained-Approve") &&
    canDecideFundAllocation(distribution, "approve");
  const canRejectAllocation =
    authrztn.includes("Retained-Approve") &&
    canDecideFundAllocation(distribution, "reject");

  // Approve/Reject stay visible but disabled when the distribution date falls
  // in a posted cutoff or no cutoff was created for it yet (server sends
  // approval_block; it blocks the request too)
  const allocationBlockReason =
    canApproveAllocation || canRejectAllocation
      ? getApprovalBlockReason(distribution)
      : null;

  // Approve (only) is also disabled while a source account can't cover what
  // approval deducts from it (Cash, plus Bank without a cheque for
  // Management); Reject moves no money, so it stays available
  const insufficientBalanceReason = getInsufficientBalanceReason(summary);

  // What still blocks approval (the server checks the same things again)
  const approvalIssues = getApprovalIssues(summary);

  // Runs one approve/reject request, then reloads the page data
  const runDecision = async (action, request) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);

    try {
      const result = await request();
      swal({
        icon: "success",
        title:
          action === "approve"
            ? "Fund allocation approved"
            : "Fund allocation rejected",
        text: result.message,
      });
    } catch (error) {
      console.log(error);
      swal({
        icon: "error",
        title: error.response?.data?.title || "Something went wrong",
        text:
          error.response?.data?.message ||
          `Unable to ${action} the fund allocation.`,
      });
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
      fetchDistribution();
      fetchFundAllocations();
    }
  };

  const handleApproveAllocation = async () => {
    if (allocationBlockReason || insufficientBalanceReason) return;
    if (approvalIssues.length > 0) {
      swal({
        icon: "warning",
        title: "Can't approve yet",
        text: approvalIssues.join("\n"),
      });
      return;
    }

    // Owners without a split are paid from the default source's cheque
    if (
      ownersOnDefault.length > 0 &&
      defaultSource.paymentMethod === "Bank" &&
      !(await validateCheckNumbers(defaultSource.chequeNumber, {
        ignore: savedCheques,
      }))
    )
      return;

    const confirmed = await swal({
      title: "Approve Fund Allocation",
      text: getApproveConfirmText(summary),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    runDecision("approve", async () => {
      // Owners without a split are saved with the default source first
      for (const owner of ownersOnDefault) {
        await saveFundAllocation({
          profit_distribution_id: id,
          owner_account_id: owner.id,
          allocated_amount: allocations[owner.id] || 0,
          lines: getOwnerLines(owner).map(toPayloadLine),
          userLoggedID,
        });
      }
      return decideFundAllocation({ id, action: "approve", userLoggedID });
    });
  };

  const handleRejectAllocation = async () => {
    const confirmed = await swal({
      title: "Reject Fund Allocation",
      text: "Reject this fund allocation? No money is moved, and the splits can be changed and approved again.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    runDecision("reject", () =>
      decideFundAllocation({ id, action: "reject", userLoggedID }),
    );
  };

  if (!authrztn.includes("Retained-View")) {
    return (
      <div className="no-access">
        <img src={NoAccess} alt="NoAccess" className="no-access-img" />
        <h3>You don't have access to this function.</h3>
      </div>
    );
  }

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link
              to="/accounting/profit-distribution"
              className="text-dark mx-2"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </Link>
            Fund Allocation
          </span>
        </div>
        {distribution && (canApproveAllocation || canRejectAllocation) && (
          // A disabled button doesn't fire hover events, so the reason
          // tooltip sits on this wrapper
          <span
            className="d-flex gap-2 align-items-center"
            title={allocationBlockReason || undefined}
          >
            {canRejectAllocation && (
              <Button
                className="btn d-flex align-items-center"
                variant="outline-danger"
                onClick={handleRejectAllocation}
                disabled={isSaving || Boolean(allocationBlockReason)}
              >
                <i className="bx bx-x-circle fs-5 me-1"></i> Reject
              </Button>
            )}
            {canApproveAllocation && (
              // Own wrapper so the insufficient balance reason shows on hover
              <span
                title={
                  allocationBlockReason
                    ? undefined
                    : insufficientBalanceReason || undefined
                }
              >
                <Button
                  className="btn d-flex align-items-center"
                  variant="success"
                  onClick={handleApproveAllocation}
                  disabled={
                    isSaving ||
                    Boolean(allocationBlockReason) ||
                    Boolean(insufficientBalanceReason)
                  }
                >
                  <i className="bx bx-check-circle fs-5 me-1"></i>
                  {isSaving ? "Processing..." : "Approve"}
                </Button>
              </span>
            )}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="w-100 p-4 d-flex justify-content-center">
          <ThreeDot color="#0d6efd" size="medium" text="" textColor="" />
        </div>
      ) : !distribution ? (
        <div className="w-100 p-4 d-flex flex-column align-items-center">
          <img src={NoData} alt="No Data" style={{ width: "150px" }} />
          <span className="text-muted mt-2">
            Profit Distribution not found.
          </span>
        </div>
      ) : (
        <div className="container-fluid mt-3">
          {allocationBlockReason && (
            <div
              className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mb-3"
              role="status"
            >
              <i className="bx bx-info-circle fs-5 flex-shrink-0"></i>
              <div className="small">
                <div className="fw-semibold">
                  Approve and Reject are disabled
                </div>
                {allocationBlockReason}
              </div>
            </div>
          )}

          {allocationApproved ? (
            <div
              className="alert alert-success d-flex align-items-start gap-2 py-2 px-3 mb-3"
              role="status"
            >
              <i className="bx bx-check-circle fs-5 flex-shrink-0"></i>
              <div className="small">
                The fund allocation was approved and can no longer be changed.
                Cash payments were posted; a Bank payment still Pending is paid
                in Issued Check or Bank Transaction.{" "}
                {distribution.status === "Distributed"
                  ? "Every payment has been paid."
                  : distribution.status === "Partially-Distributed"
                    ? "Some payments are still waiting to be paid there."
                    : "No payment has been paid yet."}
              </div>
            </div>
          ) : (
            !canEdit && (
              // Pending/Rejected can't be allocated (e.g. opened by URL)
              <div
                className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mb-3"
                role="status"
              >
                <i className="bx bx-info-circle fs-5 flex-shrink-0"></i>
                <div className="small">
                  A {distribution.status} Profit Distribution can't have its
                  funds allocated.
                </div>
              </div>
            )
          )}

          {distribution.status === "Rejected Allocated Funds" && (
            <div
              className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mb-3"
              role="status"
            >
              <i className="bx bx-info-circle fs-5 flex-shrink-0"></i>
              <div className="small">
                The fund allocation was rejected. Change the splits if needed,
                then approve it again.
              </div>
            </div>
          )}

          <ProfitDistributionSummary distribution={distribution} />

          {canEdit && (
            <>
              <h5 className="mb-0">Default Source Account</h5>
              <small className="text-muted d-block mb-2">
                Used for every owner without a split. Use Split on an owner to
                pay them from several accounts.
              </small>
            </>
          )}
          <div className="border shadow-sm rounded p-3 mb-3">
            {canEdit && (
              <SourceAccountFields
                idPrefix="default-source"
                value={defaultSource}
                onChange={handleDefaultSourceChange}
                subjects={subjects}
                accountsBySubject={accountsBySubject}
                loadAccounts={loadAccounts}
                validated={isDefaultIncomplete}
                chequeIgnore={savedCheques}
              />
            )}

            <div
              className={`d-flex flex-wrap justify-content-between align-items-center ${
                canEdit ? "mt-3 pt-3 border-top" : ""
              }`}
            >
              <span className="text-muted">Amount to Distribute</span>
              <span className="fs-5 fw-bold text-success">
                {formatToPHP(amountToDistribute)}
              </span>
            </div>

            {isDefaultIncomplete && (
              <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
                Enter the maturity date for the Bank payment.
              </div>
            )}
            {netIncome <= 0 && (
              <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
                The net income is {formatToPHP(netIncome)}, so there is nothing
                to distribute.
              </div>
            )}
          </div>

          <h5 className="mb-2">Owner's Equity</h5>
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>Owner</th>
                <th>Subject</th>
                <th>Currency</th>
                <th>Invested Amount</th>
                <th>Shared Percentage</th>
                <th>Allocated Amount</th>
                <th>Source Accounts</th>
              </tr>
            </thead>
            <tbody>
              {owners.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No Owner's Equity account found.
                  </td>
                </tr>
              ) : (
                owners.map((owner) => (
                  <tr key={owner.id}>
                    <td>{owner.account_name}</td>
                    <td>{owner.subject_name}</td>
                    <td>{owner.currency_name}</td>
                    <td>
                      {formatCurrency(
                        owner.investment_amount,
                        owner.currency_name,
                      )}
                    </td>
                    <td>{Number(owner.shared_percentage || 0).toFixed(2)}%</td>
                    <td>
                      <div className="fw-semibold text-success">
                        {formatToPHP(allocations[owner.id] || 0)}
                      </div>
                      {/* Also in the owner's own currency when it isn't PHP */}
                      {owner.currency_name?.toUpperCase() !== "PHP" &&
                        Number(owner.currency_rate) > 0 && (
                          <small className="text-muted">
                            {formatCurrency(
                              (allocations[owner.id] || 0) /
                                Number(owner.currency_rate),
                              owner.currency_name,
                            )}
                          </small>
                        )}
                    </td>
                    <td>
                      {getOwnerLines(owner).length === 0 ? (
                        <div className="small text-muted">
                          No source account yet
                        </div>
                      ) : (
                        getOwnerLines(owner).map((line) => (
                          <div key={line.account.value} className="small mb-1">
                            <div className="d-flex justify-content-between gap-3">
                              <span>{describeSource(line)}</span>
                              <span className="fw-medium text-nowrap">
                                {formatToPHP(toCents(line.amount) / 100)}
                              </span>
                            </div>
                            {line.remarks?.trim() && (
                              <div className="text-muted fst-italic">
                                {line.remarks}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      {splits[owner.id] && getUnallocatedCents(owner) > 0 && (
                        <div className="small d-flex justify-content-between gap-3 text-warning-emphasis fw-medium border-top pt-1 mt-1">
                          <span>Remaining to allocate</span>
                          <span className="text-nowrap">
                            {formatToPHP(getUnallocatedCents(owner) / 100)}
                          </span>
                        </div>
                      )}
                      {canAllocateFunds(distribution) &&
                        (allocations[owner.id] || 0) > 0 && (
                          <Button
                            variant={
                              splits[owner.id] ? "outline-primary" : "link"
                            }
                            size="sm"
                            className={splits[owner.id] ? "mt-2" : "p-0 mt-1"}
                            onClick={() => setSplitOwner(owner)}
                          >
                            <i className="bx bx-git-branch me-1"></i>
                            {splits[owner.id] ? "Edit Split" : "Split"}
                          </Button>
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {owners.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4} className="fw-bold text-end">
                    Total
                  </td>
                  <td className="fw-bold">
                    {owners
                      .reduce(
                        (sum, owner) =>
                          sum + (Number(owner.shared_percentage) || 0),
                        0,
                      )
                      .toFixed(2)}
                    %
                  </td>
                  <td className="fw-bold text-success">
                    {formatToPHP(
                      Object.values(allocations).reduce(
                        (sum, amount) => sum + amount,
                        0,
                      ),
                    )}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </Table>

          {unfundedOwners.length > 0 && (
            <div className="alert alert-warning py-2 px-3 small">
              Choose a default source account or split the allocation for:{" "}
              {unfundedOwners.map((owner) => owner.account_name).join(", ")}.
            </div>
          )}

          {partiallySplitOwners.length > 0 && (
            <div className="alert alert-warning py-2 px-3 small">
              Still to allocate:{" "}
              {partiallySplitOwners
                .map(
                  (owner) =>
                    `${owner.account_name} (${formatToPHP(
                      getUnallocatedCents(owner) / 100,
                    )})`,
                )
                .join(", ")}
              . Use Edit Split to finish.
            </div>
          )}

          {/* {overdrawnAccounts.map((usage) => (
            <div
              key={usage.account.id}
              className="alert alert-danger py-2 px-3 small"
            >
              {describeOverdrawnAccount(usage)}.
            </div>
          ))} */}
        </div>
      )}

      <SplitAllocationModal
        show={Boolean(splitOwner)}
        owner={splitOwner}
        allocatedAmount={splitOwner ? allocations[splitOwner.id] || 0 : 0}
        initialLines={splitOwner ? getOwnerLines(splitOwner) : []}
        hasCustomSplit={Boolean(splitOwner && splits[splitOwner.id])}
        subjects={subjects}
        accountsBySubject={accountsBySubject}
        loadAccounts={loadAccounts}
        onClose={() => {
          if (!isSavingRef.current) setSplitOwner(null);
        }}
        onSave={handleSaveSplit}
        onReset={handleResetSplit}
        savedCheques={savedCheques}
        isSaving={isSaving}
      />
    </div>
  );
}

export default FundAllocation;
